<template>
  <v-container fluid>
    <v-card>
      <v-toolbar dense style="background:#F5F5F5">
        <v-toolbar-title dense style="color:#607D8B;font-weight:bold">Statistiques journalières des caissiers</v-toolbar-title>
        <v-spacer></v-spacer>
        <!-- AJOUT: Bouton de rafraîchissement -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
                icon
                @click="refreshStats"
                :disabled="!canRefresh"
                :loading="refreshing"
                v-bind="attrs"
                v-on="on"
            >
              <v-icon :color="canRefresh ? '#009688' : 'grey'">mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>{{ canRefresh ? 'Actualiser les données' : 'Sélectionnez tous les critères' }}</span>
        </v-tooltip>
      </v-toolbar>

      <v-card-text>
        <!-- Loading Dialog -->
        <v-dialog fullscreen v-model="loading">
          <v-container fill-height fluid style="background-color: rgba(200, 200, 200, 0.5);">
            <v-layout align-center="true" justify-center="true">
              <v-progress-circular
                  :size="70"
                  color="#009688"
                  indeterminate>
              </v-progress-circular>
            </v-layout>
          </v-container>
        </v-dialog>

        <!-- Alert -->
        <v-alert :color="AlertColor(type)"
                 :dismissible="true"
                 :value="true"
                 @input="dismissible_close"
                 border="top"
                 class="pre-formatted multi-line"
                 colored-border
                 elevation="2"
                 icon="check_circle"
                 style="color: #546E7A"
                 v-if="type">
          {{ text }}
        </v-alert>

        <!-- Filtres -->
        <v-card class="mt-1" flat tile>
          <v-card-text class="pb-0">
            <!-- AJOUT: Toggle pour le mode de date avec loaders -->
            <v-row no-gutters class="mb-4">
              <v-col cols="12" class="d-flex justify-center">
                <v-btn-toggle
                    v-model="dateMode"
                    mandatory
                    color="primary"
                    class="date-mode-toggle"
                >
                  <v-btn
                      value="single"
                      large
                      :loading="dateMode === 'single' && loadingSingleMode"
                      :disabled="loadingRangeMode"
                  >
                    <v-icon left v-if="!loadingSingleMode">mdi-calendar-today</v-icon>
                    Jour unique
                  </v-btn>
                  <v-btn
                      value="range"
                      large
                      :loading="dateMode === 'range' && loadingRangeMode"
                      :disabled="loadingSingleMode"
                  >
                    <v-icon left v-if="!loadingRangeMode">mdi-calendar-range</v-icon>
                    Période
                  </v-btn>
                </v-btn-toggle>
              </v-col>
            </v-row>

            <v-row align="center" no-gutters>
              <v-col cols="12" sm="6" md="4" class="pr-sm-2">
                <v-select
                    :items="restaurants"
                    :disabled="disableMainBtn || restaurants.length === 0"
                    v-model="selectedRestaurant"
                    @change="onRestaurantChange"
                    item-text="name"
                    item-value="id"
                    label="Restaurant / Entreprise"
                    prepend-icon="mdi-store"
                    hide-details
                    clearable
                    dense
                    outlined
                ></v-select>
              </v-col>

              <v-col cols="12" sm="6" md="4" class="pr-sm-2 pt-2 pt-sm-0">
                <v-select
                    :items="cashiers"
                    :disabled="disableMainBtn || !selectedRestaurant || cashiers.length === 0"
                    v-model="selectedCashier"
                    @change="onCashierChange"
                    item-text="displayName"
                    item-value="id"
                    label="Caissier"
                    prepend-icon="mdi-account-cash"
                    hide-details
                    clearable
                    dense
                    outlined
                ></v-select>
              </v-col>

              <v-col cols="12" sm="12" md="4" class="pt-2 pt-md-0">
                <!-- Mode jour unique -->
                <v-menu
                    v-if="dateMode === 'single'"
                    ref="menu"
                    v-model="menu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                        v-model="dateFormatted"
                        label="Date"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        hide-details
                        dense
                        outlined
                    ></v-text-field>
                  </template>
                  <v-date-picker
                      v-model="selectedDate"
                      :max="new Date().toISOString().substr(0, 10)"
                      @input="onDateChange"
                      locale="fr-fr"
                      first-day-of-week="1"
                      :day-format="date => new Date(date).getDate()"
                      :header-date-format="formatHeaderDate"
                      :title-date-format="formatTitleDate"
                  ></v-date-picker>
                </v-menu>

                <!-- Mode période -->
                <v-row v-if="dateMode === 'range'" no-gutters>
                  <v-col cols="12" sm="6" class="pr-sm-1">
                    <v-menu
                        ref="menuDateStart"
                        v-model="menuDateStart"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            v-model="dateStartFormatted"
                            label="Du"
                            prepend-icon="mdi-calendar-start"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            :hide-details="!dateRangeError"
                            :error-messages="dateRangeError"
                            dense
                            outlined
                        ></v-text-field>
                      </template>
                      <v-date-picker
                          v-model="selectedDateRange.start"
                          :max="new Date().toISOString().substr(0, 10)"
                          @input="onDateRangeChange"
                          locale="fr-fr"
                          first-day-of-week="1"
                          :day-format="date => new Date(date).getDate()"
                          :header-date-format="formatHeaderDate"
                          :title-date-format="formatTitleDate"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>

                  <v-col cols="12" sm="6" class="pl-sm-1 pt-2 pt-sm-0">
                    <v-menu
                        ref="menuDateEnd"
                        v-model="menuDateEnd"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            v-model="dateEndFormatted"
                            label="Au"
                            prepend-icon="mdi-calendar-end"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            hide-details
                            dense
                            outlined
                        ></v-text-field>
                      </template>
                      <v-date-picker
                          v-model="selectedDateRange.end"
                          :min="selectedDateRange.start"
                          :max="new Date().toISOString().substr(0, 10)"
                          @input="onDateRangeChange"
                          locale="fr-fr"
                          first-day-of-week="1"
                          :day-format="date => new Date(date).getDate()"
                          :header-date-format="formatHeaderDate"
                          :title-date-format="formatTitleDate"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <!-- NOUVEAU: Bouton de recherche pour le mode période -->
            <v-row v-if="dateMode === 'range'" class="mt-3">
              <v-col cols="12" class="d-flex justify-center">
                <v-btn
                    color="primary"
                    large
                    elevation="2"
                    :disabled="!canSearchRange"
                    :loading="loading"
                    @click="getStats()"
                    class="search-btn"
                >
                  <v-icon left>mdi-magnify</v-icon>
                  Rechercher les statistiques
                </v-btn>
              </v-col>
            </v-row>

            <!-- Indicateur de statut avec dernière mise à jour -->
            <v-row v-if="selectedRestaurant && selectedCashier && ((dateMode === 'single' && selectedDate) || (dateMode === 'range' && selectedDateRange.start && selectedDateRange.end && isDateRangeValid))" class="mt-3">
              <v-col cols="12">
                <div class="status-indicator">
                  <v-icon color="#009688" size="16" class="mr-1">mdi-information</v-icon>
                  <span class="text-caption">
                    <span v-if="dateMode === 'single'">
                      Les statistiques se chargent automatiquement lorsque vous modifiez les filtres
                    </span>
                    <span v-else>
                      Cliquez sur "Rechercher" pour charger les statistiques de la période
                    </span>
                    <span v-if="lastRefreshTime" class="ml-2">
                      • Dernière mise à jour : {{ formatLastRefresh() }}
                    </span>
                  </span>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Résultats -->
        <div v-if="statsData">
          <!-- Section 1 - Résumé de la session avec design moderne -->
          <v-row class="mt-6">
            <v-col cols="12">
              <v-card elevation="0" class="modern-summary-card">
                <div class="card-header-gradient">
                  <div class="header-content">
                    <v-icon size="32" color="white" class="mr-3">mdi-chart-pie</v-icon>
                    <span class="header-title">
                      Ventes
                      <span v-if="dateMode === 'single'">du {{ formatDate(selectedDate) }}</span>
                      <span v-else>du {{ formatDate(selectedDateRange.start) }} au {{ formatDate(selectedDateRange.end) }}</span>
                      - {{ getCashierName() }}
                    </span>
                  </div>
                </div>

                <v-card-text class="pa-6">
                  <!-- AJOUT: Indicateur de période si mode range -->
                  <div v-if="dateMode === 'range'" class="period-indicator mb-4">
                    <v-icon size="20" color="#607D8B" class="mr-2">mdi-calendar-range</v-icon>
                    <span class="text-subtitle-2">
                      Période analysée : {{ getPeriodDays() }} jours
                    </span>
                  </div>

                  <v-row>
                    <v-col cols="12" md="4">
                      <div class="metric-card transactions-card">
                        <div class="metric-icon">
                          <v-icon size="28" color="#2196F3">mdi-receipt</v-icon>
                        </div>
                        <div class="metric-content">
                          <div class="metric-value primary--text">{{ statsData.nombreTransactions }}</div>
                          <div class="metric-label">Plats vendus</div>
                        </div>
                      </div>
                    </v-col>

                    <v-col cols="12" md="4">
                      <div class="metric-card total-card">
                        <div class="metric-icon">
                          <v-icon size="28" color="#4CAF50">mdi-cash-multiple</v-icon>
                        </div>
                        <div class="metric-content">
                          <div class="metric-value success--text">{{ statsData.montantTotalEncaisse }}</div>
                          <div class="metric-label">CA Global</div>
                        </div>
                      </div>
                    </v-col>

                    <v-col cols="12" md="4">
                      <div class="metric-card external-card">
                        <div class="metric-icon">
                          <v-icon size="28" color="#FF9800">mdi-wallet</v-icon>
                        </div>
                        <div class="metric-content">
                          <div class="metric-value warning--text">{{ calculateTotalToReverse() }}</div>
                          <div class="metric-label">À reverser</div>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Section 2 & 3 - Ventes et Recharges avec nouveau design -->
          <v-row class="mt-6">
            <!-- Ventes -->
            <v-col cols="12" lg="6">
              <v-card elevation="0" class="glass-card sales-card">
                <div class="glass-header sales-header">
                  <v-icon size="24" color="white" class="mr-2">mdi-point-of-sale</v-icon>
                  <span class="glass-title">Paiements comptants</span>
                </div>

                <v-card-text class="pa-6">
                  <div class="stats-overview mb-6">
                    <div class="overview-item">
                      <div class="overview-number success--text">{{ getExternalPaymentsCount() }}</div>
                      <div class="overview-label">Nombre paiements comptants</div>
                    </div>
                    <div class="overview-item">
                      <div class="overview-number success--text">{{ getExternalPaymentsTotal() }}</div>
                      <div class="overview-label">Total comptants encaissés</div>
                    </div>
                  </div>

                  <!-- Nouveau design pour afficher les données sans graphique -->
                  <div class="data-visualization">
                    <h4 class="visualization-title">Répartition par méthode de paiement</h4>

                    <!-- Message d'avertissement si incohérence détectée -->
                    <v-alert
                        v-if="hasSalesDataInconsistency()"
                        type="warning"
                        dense
                        outlined
                        class="mb-4"
                    >
                      <v-icon slot="prepend" size="20">mdi-alert</v-icon>
                      Attention: Le total des paiements comptants ({{ statsData.totalComptantsEncaisses }}) ne correspond pas à la somme des méthodes de paiement.
                      Veuillez vérifier les données avec le service technique.
                    </v-alert>

                    <!-- Cash -->
                    <div class="payment-stat-item">
                      <div class="payment-stat-header">
                        <div class="payment-stat-icon cash-icon">
                          <v-icon size="20" color="#4CAF50">mdi-cash</v-icon>
                        </div>
                        <span class="payment-stat-name">Cash</span>
                        <span class="payment-stat-amount success--text">{{ statsData.cashVentes || '0 fcfa' }}</span>
                      </div>
                      <v-progress-linear
                          :value="getPercentage(statsData.cashVentes, getExternalPaymentsTotalValue())"
                          height="8"
                          rounded
                          color="#4CAF50"
                          background-color="rgba(76, 175, 80, 0.1)"
                      ></v-progress-linear>
                      <div class="payment-stat-percent">{{ getPercentage(statsData.cashVentes, getExternalPaymentsTotalValue()) }}%</div>
                    </div>

                    <!-- Wave -->
                    <div class="payment-stat-item">
                      <div class="payment-stat-header">
                        <div class="payment-stat-icon wave-icon">
                          <v-icon size="20" color="#2196F3">mdi-wave</v-icon>
                        </div>
                        <span class="payment-stat-name">Wave</span>
                        <span class="payment-stat-amount primary--text">{{ statsData.waveVentes || '0 fcfa' }}</span>
                      </div>
                      <v-progress-linear
                          :value="getPercentage(statsData.waveVentes, getExternalPaymentsTotalValue())"
                          height="8"
                          rounded
                          color="#2196F3"
                          background-color="rgba(33, 150, 243, 0.1)"
                      ></v-progress-linear>
                      <div class="payment-stat-percent">{{ getPercentage(statsData.waveVentes, getExternalPaymentsTotalValue()) }}%</div>
                    </div>

                    <!-- Orange Money -->
                    <div class="payment-stat-item">
                      <div class="payment-stat-header">
                        <div class="payment-stat-icon om-icon">
                          <v-icon size="20" color="#FF9800">mdi-cellphone</v-icon>
                        </div>
                        <span class="payment-stat-name">Orange Money</span>
                        <span class="payment-stat-amount warning--text">{{ statsData.omVentes || '0 fcfa' }}</span>
                      </div>
                      <v-progress-linear
                          :value="getPercentage(statsData.omVentes, getExternalPaymentsTotalValue())"
                          height="8"
                          rounded
                          color="#FF9800"
                          background-color="rgba(255, 152, 0, 0.1)"
                      ></v-progress-linear>
                      <div class="payment-stat-percent">{{ getPercentage(statsData.omVentes, getExternalPaymentsTotalValue()) }}%</div>
                    </div>

                    <!-- Autres -->
                    <div class="payment-stat-item">
                      <div class="payment-stat-header">
                        <div class="payment-stat-icon other-icon">
                          <v-icon size="20" color="#9E9E9E">mdi-dots-horizontal</v-icon>
                        </div>
                        <span class="payment-stat-name">Autres</span>
                        <span class="payment-stat-amount grey--text">{{ statsData.autreVentes || '0 fcfa' }}</span>
                      </div>
                      <v-progress-linear
                          :value="getPercentage(statsData.autreVentes, getExternalPaymentsTotalValue())"
                          height="8"
                          rounded
                          color="#9E9E9E"
                          background-color="rgba(158, 158, 158, 0.1)"
                      ></v-progress-linear>
                      <div class="payment-stat-percent">{{ getPercentage(statsData.autreVentes, getExternalPaymentsTotalValue()) }}%</div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Recharges -->
            <v-col cols="12" lg="6">
              <v-card elevation="0" class="glass-card recharge-card">
                <div class="glass-header recharge-header">
                  <v-icon size="24" color="white" class="mr-2">mdi-battery-charging</v-icon>
                  <span class="glass-title">Recharges</span>
                </div>

                <v-card-text class="pa-6">
                  <div class="stats-overview mb-6">
                    <div class="overview-item">
                      <div class="overview-number orange--text">{{ statsData.nombreRecharges }}</div>
                      <div class="overview-label">Recharges</div>
                    </div>
                    <div class="overview-item">
                      <div class="overview-number orange--text">{{ statsData.montantRecharges }}</div>
                      <div class="overview-label">Montant total</div>
                    </div>
                  </div>

                  <!-- Nouveau design pour les recharges -->
                  <div class="data-visualization">
                    <h4 class="visualization-title">Répartition par méthode de paiement</h4>

                    <!-- Message d'avertissement si incohérence détectée -->
                    <v-alert
                        v-if="hasRechargeDataInconsistency()"
                        type="warning"
                        dense
                        outlined
                        class="mb-4"
                    >
                      <v-icon slot="prepend" size="20">mdi-alert</v-icon>
                      Attention: Le montant total des recharges ({{ statsData.montantRecharges }}) ne correspond pas à la somme des méthodes de paiement.
                      Veuillez vérifier les données avec le service technique.
                    </v-alert>

                    <!-- Cash -->
                    <div class="payment-stat-item">
                      <div class="payment-stat-header">
                        <div class="payment-stat-icon cash-icon">
                          <v-icon size="20" color="#4CAF50">mdi-cash</v-icon>
                        </div>
                        <span class="payment-stat-name">Cash</span>
                        <span class="payment-stat-amount success--text">{{ statsData.cashRecharges || '0 fcfa' }}</span>
                      </div>
                      <v-progress-linear
                          :value="getPercentage(statsData.cashRecharges, statsData.montantRecharges)"
                          height="8"
                          rounded
                          color="#4CAF50"
                          background-color="rgba(76, 175, 80, 0.1)"
                      ></v-progress-linear>
                      <div class="payment-stat-percent">{{ getPercentage(statsData.cashRecharges, statsData.montantRecharges) }}%</div>
                    </div>

                    <!-- Wave -->
                    <div class="payment-stat-item">
                      <div class="payment-stat-header">
                        <div class="payment-stat-icon wave-icon">
                          <v-icon size="20" color="#2196F3">mdi-wave</v-icon>
                        </div>
                        <span class="payment-stat-name">Wave</span>
                        <span class="payment-stat-amount primary--text">{{ statsData.waveRecharges || '0 fcfa' }}</span>
                      </div>
                      <v-progress-linear
                          :value="getPercentage(statsData.waveRecharges, statsData.montantRecharges)"
                          height="8"
                          rounded
                          color="#2196F3"
                          background-color="rgba(33, 150, 243, 0.1)"
                      ></v-progress-linear>
                      <div class="payment-stat-percent">{{ getPercentage(statsData.waveRecharges, statsData.montantRecharges) }}%</div>
                    </div>

                    <!-- Orange Money -->
                    <div class="payment-stat-item">
                      <div class="payment-stat-header">
                        <div class="payment-stat-icon om-icon">
                          <v-icon size="20" color="#FF9800">mdi-cellphone</v-icon>
                        </div>
                        <span class="payment-stat-name">Orange Money</span>
                        <span class="payment-stat-amount warning--text">{{ statsData.omRecharges || '0 fcfa' }}</span>
                      </div>
                      <v-progress-linear
                          :value="getPercentage(statsData.omRecharges, statsData.montantRecharges)"
                          height="8"
                          rounded
                          color="#FF9800"
                          background-color="rgba(255, 152, 0, 0.1)"
                      ></v-progress-linear>
                      <div class="payment-stat-percent">{{ getPercentage(statsData.omRecharges, statsData.montantRecharges) }}%</div>
                    </div>

                    <!-- Autres -->
                    <div class="payment-stat-item">
                      <div class="payment-stat-header">
                        <div class="payment-stat-icon other-icon">
                          <v-icon size="20" color="#9E9E9E">mdi-dots-horizontal</v-icon>
                        </div>
                        <span class="payment-stat-name">Autres</span>
                        <span class="payment-stat-amount grey--text">{{ statsData.autreRecharges || '0 fcfa' }}</span>
                      </div>
                      <v-progress-linear
                          :value="getPercentage(statsData.autreRecharges, statsData.montantRecharges)"
                          height="8"
                          rounded
                          color="#9E9E9E"
                          background-color="rgba(158, 158, 158, 0.1)"
                      ></v-progress-linear>
                      <div class="payment-stat-percent">{{ getPercentage(statsData.autreRecharges, statsData.montantRecharges) }}%</div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Timeline section moderne -->
          <v-row class="mt-6" v-if="statsData.date1stTrs || statsData.dateLastTrs">
            <v-col cols="12">
              <v-card elevation="0" class="timeline-card">
                <v-card-text class="pa-6">
                  <div class="timeline-container">
                    <div class="timeline-title">
                      <v-icon size="24" color="#607D8B" class="mr-2">mdi-timeline-clock</v-icon>
                      Chronologie des transactions
                    </div>

                    <div class="timeline-content">
                      <div class="timeline-item" v-if="statsData.date1stTrs">
                        <div class="timeline-dot start-dot"></div>
                        <div class="timeline-info">
                          <div class="timeline-label">Première transaction</div>
                          <div class="timeline-time">{{ formatDateTime(statsData.date1stTrs) }}</div>
                        </div>
                      </div>

                      <div class="timeline-line" v-if="statsData.date1stTrs && statsData.dateLastTrs"></div>

                      <div class="timeline-item" v-if="statsData.dateLastTrs">
                        <div class="timeline-dot end-dot"></div>
                        <div class="timeline-info">
                          <div class="timeline-label">Dernière transaction</div>
                          <div class="timeline-time">{{ formatDateTime(statsData.dateLastTrs) }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Message si pas de données -->
        <v-row class="mt-4" v-if="!statsData && searchPerformed">
          <v-col cols="12">
            <v-card elevation="2">
              <v-card-text class="text-center">
                <v-icon size="64" color="grey">mdi-chart-bar</v-icon>
                <div class="subtitle-1 mt-2">Aucune donnée trouvée pour cette recherche</div>
                <div class="body-2 grey--text">Vérifiez les critères de recherche et réessayez</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import apiClient from "../../../config/api.js";
import moment from "moment";
import 'moment/locale/fr'; // Import du français pour moment

// Configurer moment en français
moment.locale('fr');

export default {
  name: "CashierDailyStats",
  data: function () {
    return {
      disableMainBtn: false,
      loading: false,
      text: "",
      color: "",
      type: null,
      elapse: null,

      // Filtres
      restaurants: [],
      cashiers: [],
      selectedRestaurant: null,
      selectedCashier: null,
      selectedDate: new Date().toISOString().substr(0, 10),
      menu: false,

      // AJOUT: Mode de sélection de date
      dateMode: 'single', // 'single' ou 'range'
      selectedDateRange: {
        start: new Date().toISOString().substr(0, 10),
        end: new Date().toISOString().substr(0, 10)
      },
      menuDateStart: false,
      menuDateEnd: false,

      // NOUVEAU: Variables pour les loaders des modes
      loadingSingleMode: false,
      loadingRangeMode: false,

      // Données
      statsData: null,
      searchPerformed: false,

      // AJOUT: Variables pour le refresh
      refreshing: false,
      lastRefreshTime: null,
    }
  },

  computed: {
    dateFormatted() {
      return this.selectedDate ? this.formatDate(this.selectedDate) : '';
    },
    // AJOUT: Formatage des dates pour le mode range
    dateStartFormatted() {
      return this.selectedDateRange.start ? this.formatDate(this.selectedDateRange.start) : '';
    },
    dateEndFormatted() {
      return this.selectedDateRange.end ? this.formatDate(this.selectedDateRange.end) : '';
    },
    // AJOUT: Validation de la plage de dates
    isDateRangeValid() {
      if (this.dateMode !== 'range') return true;

      const start = moment(this.selectedDateRange.start);
      const end = moment(this.selectedDateRange.end);

      // Vérifier que la date de fin n'est pas avant la date de début
      if (end.isBefore(start)) return false;

      // Vérifier que la plage ne dépasse pas 31 jours (inclusif)
      const daysDiff = end.diff(start, 'days') + 1; // +1 pour inclure les deux dates
      return daysDiff <= 31;
    },
    // AJOUT: Message d'erreur pour la plage de dates
    dateRangeError() {
      if (this.isDateRangeValid) return null;

      const start = moment(this.selectedDateRange.start);
      const end = moment(this.selectedDateRange.end);

      if (end.isBefore(start)) {
        return 'La date de fin doit être après la date de début';
      }

      const daysDiff = end.diff(start, 'days') + 1; // +1 pour inclure les deux dates
      if (daysDiff > 31) {
        return `La période ne peut pas dépasser 31 jours (actuellement ${daysDiff} jours)`;
      }

      return null;
    },
    // AJOUT: Computed pour savoir si on peut rafraîchir
    canRefresh() {
      const hasDateSelection = this.dateMode === 'single'
          ? this.selectedDate
          : (this.selectedDateRange.start && this.selectedDateRange.end && this.isDateRangeValid);

      return this.selectedRestaurant && this.selectedCashier && hasDateSelection && !this.refreshing;
    },
    // NOUVEAU: Computed pour savoir si on peut faire une recherche en mode range
    canSearchRange() {
      return this.selectedRestaurant &&
          this.selectedCashier &&
          this.selectedDateRange.start &&
          this.selectedDateRange.end &&
          this.isDateRangeValid &&
          !this.loading;
    }
  },

  created() {
    this.loadRestaurants();
  },

  watch: {
    // MODIFIÉ: Watcher pour réagir au changement de mode avec loaders
    dateMode(newMode, oldMode) {
      // Activer le loader pour le nouveau mode
      if (newMode === 'single') {
        this.loadingSingleMode = true;
        setTimeout(() => {
          this.loadingSingleMode = false;
        }, 800); // Simuler un petit délai de chargement
      } else if (newMode === 'range') {
        this.loadingRangeMode = true;
        setTimeout(() => {
          this.loadingRangeMode = false;
        }, 800); // Simuler un petit délai de chargement
      }

      // Réinitialiser les données quand on change de mode
      this.statsData = null;
      this.searchPerformed = false;

      // Si on passe en mode range, initialiser les dates de début et fin avec la date actuelle
      if (newMode === 'range' && this.selectedDate) {
        this.selectedDateRange.start = this.selectedDate;
        this.selectedDateRange.end = this.selectedDate;
      }
    }
  },

  methods: {
    loadRestaurants() {
      this.loading = true;
      const companyId = process.env.VUE_APP_APP_COMPANY_ID;

      apiClient
          .get(`${this.$store.getters.user.baseUrl}/wallet/get_wallet_restaurants_affiliated_to_company/${companyId}/${sessionStorage.getItem("accountId")}`)
          .then(response => {
            this.loading = false;

            if (response.data.status === 200) {
              this.restaurants = response.data.datas || [];

              // Auto-sélection si un seul restaurant
              if (this.restaurants.length === 1) {
                this.selectedRestaurant = this.restaurants[0].id;
                this.onRestaurantChange();
              }
            } else {
              this.showAlert('warning', response.data.message || 'Erreur lors du chargement des restaurants');
            }
          })
          .catch(error => {
            this.loading = false;
            this.showAlert('warning', 'Erreur lors du chargement des restaurants: ' + error.message);
          });
    },

    onRestaurantChange() {
      if (!this.selectedRestaurant) {
        this.cashiers = [];
        this.selectedCashier = null;
        this.statsData = null;
        this.searchPerformed = false;
        return;
      }

      this.loadCashiers();
      // Réinitialiser les données quand on change de restaurant
      this.statsData = null;
      this.searchPerformed = false;
    },

    loadCashiers() {
      this.loading = true;

      // Utiliser le nouvel endpoint avec filtre direct en base
      apiClient
          .get(`${this.$store.getters.user.baseUrl}/account/get_accounts_by_company_and_role/${this.selectedRestaurant}/CAISSIER/${sessionStorage.getItem("accountId")}`)
          .then(response => {
            this.loading = false;

            if (response.data.status === 200) {
              const cashiersData = response.data.datas || [];

              this.cashiers = cashiersData.map(cashier => ({
                id: cashier.id,
                displayName: `${cashier.accountLastname.toUpperCase()} ${this.capitalize(cashier.accountFirstname)} (${cashier.userId})`,
                userId: cashier.userId
              }));

              console.log('Caissiers trouvés:', this.cashiers.length);
            } else {
              this.showAlert('warning', response.data.message || 'Erreur lors du chargement des caissiers');
              this.cashiers = [];
            }

            this.selectedCashier = null;
          })
          .catch(error => {
            this.loading = false;
            this.cashiers = [];
            this.selectedCashier = null;
            this.showAlert('warning', 'Erreur lors du chargement des caissiers: ' + error.message);
          });
    },

    onCashierChange() {
      // MODIFIÉ: Ne charger automatiquement que pour le mode single
      if (this.selectedCashier && this.selectedRestaurant && this.dateMode === 'single') {
        if (this.selectedDate) {
          this.getStats();
        }
      } else if (!this.selectedCashier) {
        // Si on efface le caissier, effacer les données
        this.statsData = null;
        this.searchPerformed = false;
      }
    },

    onDateChange() {
      this.menu = false;
      // INCHANGÉ: Chargement automatique pour le mode single
      if (this.selectedCashier && this.selectedRestaurant && this.selectedDate) {
        this.getStats();
      }
    },

    // MODIFIÉ: Ne plus charger automatiquement pour le mode range
    onDateRangeChange() {
      // Fermer les menus
      if (this.menuDateStart) this.menuDateStart = false;
      if (this.menuDateEnd) this.menuDateEnd = false;

      // Ne plus charger automatiquement - l'utilisateur devra cliquer sur le bouton Rechercher
      // Les données seront chargées uniquement via le bouton de recherche
    },

    // AJOUT: Méthode pour rafraîchir les données
    refreshStats() {
      if (!this.canRefresh) return;

      this.refreshing = true;
      console.log('🔄 Rafraîchissement des données...');

      // Forcer le rechargement des stats
      this.getStats(true);
    },

    getStats(isRefresh = false) {
      // Validation selon le mode
      if (this.dateMode === 'single') {
        if (!this.selectedRestaurant || !this.selectedCashier || !this.selectedDate) {
          this.showAlert('warning', 'Veuillez remplir tous les champs de recherche');
          return;
        }
      } else {
        if (!this.selectedRestaurant || !this.selectedCashier ||
            !this.selectedDateRange.start || !this.selectedDateRange.end) {
          this.showAlert('warning', 'Veuillez remplir tous les champs de recherche');
          return;
        }

        if (!this.isDateRangeValid) {
          this.showAlert('warning', this.dateRangeError);
          return;
        }
      }

      this.loading = !isRefresh; // Si c'est un refresh, on utilise refreshing au lieu de loading
      this.statsData = null;
      this.searchPerformed = true;

      // Construire l'URL selon le mode
      let url;
      if (this.dateMode === 'single') {
        url = `${this.$store.getters.user.baseUrl}/wallet/get_cashier_daily_stats/${this.selectedDate}/${this.selectedCashier}/${sessionStorage.getItem("accountId")}`;
      } else {
        // Utiliser le nouvel endpoint pour les périodes
        url = `${this.$store.getters.user.baseUrl}/wallet/get_cashier_period_stats/${this.selectedDateRange.start}/${this.selectedDateRange.end}/${this.selectedCashier}/${sessionStorage.getItem("accountId")}`;
      }

      apiClient
          .get(url)
          .then(response => {
            this.loading = false;
            this.refreshing = false;

            if (response.data && response.data.status === 200) {
              this.statsData = response.data.datas;
              console.log('📊 Données reçues du backend:', this.statsData);
              console.log('   - nombrePaiementsComptants:', this.statsData.nombrePaiementsComptants);
              console.log('   - totalComptantsEncaisses:', this.statsData.totalComptantsEncaisses);

              // DEBUG: Afficher les valeurs détaillées
              console.log('   📍 VALEURS PAR MÉTHODE:');
              console.log('   - cashVentes:', this.statsData.cashVentes);
              console.log('   - waveVentes:', this.statsData.waveVentes);
              console.log('   - omVentes:', this.statsData.omVentes);
              console.log('   - autreVentes:', this.statsData.autreVentes);

              // AJOUT: Mettre à jour l'heure du dernier refresh
              this.lastRefreshTime = new Date();

              const message = isRefresh ? 'Données actualisées avec succès' : 'Statistiques chargées avec succès';
              this.showAlert('info', message);
            } else {
              this.showAlert('warning', response.data ? response.data.message : 'Réponse backend invalide');
            }
          })
          .catch(error => {
            this.loading = false;
            this.refreshing = false;

            if (error.response && error.response.data) {
              const errorData = error.response.data;
              if (errorData && errorData.errors && Array.isArray(errorData.errors)) {
                const firstError = errorData.errors[0];
                if (typeof firstError === 'string') {
                  this.showAlert('warning', 'Erreur backend: ' + firstError);
                } else if (firstError && firstError.message) {
                  this.showAlert('warning', 'Erreur backend: ' + firstError.message);
                } else {
                  this.showAlert('warning', 'Erreur backend: ' + JSON.stringify(firstError));
                }
              } else if (errorData && errorData.message) {
                this.showAlert('warning', 'Erreur: ' + errorData.message);
              } else {
                this.showAlert('warning', 'Erreur serveur non identifiée');
              }
            } else {
              this.showAlert('warning', 'Erreur lors du chargement des statistiques: ' + error.message);
            }
          });
    },

    getPercentage(value, total) {
      if (!value || !total) return 0;
      const numValue = this.extractNumber(value);
      const numTotal = typeof total === 'number' ? total : this.extractNumber(total);
      if (numTotal === 0) return 0;
      const percentage = Math.round((numValue / numTotal) * 100);
      // Désactivé temporairement jusqu'à la correction backend
      // if (numTotal > 0 && percentage === 0) {
      //   console.warn(`⚠️ Attention: Total de ${numTotal} mais valeur individuelle de ${numValue}`);
      // }
      return percentage;
    },
    extractNumber(value) {
      if (!value) return 0;
      const numStr = value.toString().replace(/[^\d]/g, '');
      return parseInt(numStr) || 0;
    },

    formatDate(date) {
      return moment(date).format("DD/MM/YYYY");
    },

    formatHeaderDate(date) {
      return moment(date).locale('fr').format("MMMM YYYY");
    },

    formatTitleDate(date) {
      return moment(date).locale('fr').format("dddd D MMMM YYYY");
    },

    formatDateTime(dateTime) {
      if (!dateTime) return '';
      return moment(dateTime).format("DD/MM/YYYY HH:mm:ss");
    },

    // AJOUT: Formater le temps depuis le dernier refresh
    formatLastRefresh() {
      if (!this.lastRefreshTime) return '';
      return moment(this.lastRefreshTime).fromNow();
    },

    capitalize(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    },

    AlertColor(type) {
      return type === "info" ? "#4CAF50" : "#F44336";
    },

    showAlert(type, message) {
      this.type = type;
      this.text = message;

      let timer = this.showAlert.timer;
      if (timer) {
        clearTimeout(timer);
      }
      this.showAlert.timer = setTimeout(() => {
        this.type = null;
      }, 10000);

      this.elapse = 1;
      let t = this.showAlert.t;
      if (t) {
        clearInterval(t);
      }

      this.showAlert.t = setInterval(() => {
        if (this.elapse === 3) {
          this.elapse = 0;
          clearInterval(this.showAlert.t);
        } else {
          this.elapse++;
        }
      }, 3000);
    },

    dismissible_close() {
      this.elapse = 3;
      clearTimeout(this.showAlert.timer);
      clearInterval(this.showAlert.t);
      this.type = null;
    },

    hasRechargeDataInconsistency() {
      if (!this.statsData) return false;

      const total = this.extractNumber(this.statsData.montantRecharges);
      const cash = this.extractNumber(this.statsData.cashRecharges);
      const wave = this.extractNumber(this.statsData.waveRecharges);
      const om = this.extractNumber(this.statsData.omRecharges);
      const autres = this.extractNumber(this.statsData.autreRecharges);

      const sum = cash + wave + om + autres;

      // Si le total est supérieur à 0 mais la somme est 0, il y a une incohérence
      return total > 0 && sum === 0;
    },

    hasSalesDataInconsistency() {
      if (!this.statsData) return false;

      // Utilise maintenant totalComptantsEncaisses du backend
      const total = this.extractNumber(this.statsData.totalComptantsEncaisses);
      const cash = this.extractNumber(this.statsData.cashVentes);
      const wave = this.extractNumber(this.statsData.waveVentes);
      const om = this.extractNumber(this.statsData.omVentes);
      const autres = this.extractNumber(this.statsData.autreVentes);

      const sum = cash + wave + om + autres;

      // Si les totaux ne correspondent pas
      return Math.abs(total - sum) > 1; // Tolérance de 1 FCFA pour les arrondis
    },

    getCashierName() {
      if (!this.selectedCashier || !this.cashiers.length) return '';
      const cashier = this.cashiers.find(c => c.id === this.selectedCashier);
      return cashier ? cashier.displayName.split(' (')[0] : '';
    },

    getExternalPaymentsCount() {
      // Utilise maintenant le champ du backend
      if (!this.statsData) return 0;

      // Si le nouveau champ n'existe pas, essayer de calculer
      if (this.statsData.nombrePaiementsComptants !== undefined) {
        return this.statsData.nombrePaiementsComptants;
      }

      // Fallback : estimation basée sur montantTotalEncaisseExternal
      console.warn('⚠️ nombrePaiementsComptants non trouvé dans les données');
      return this.extractNumber(this.statsData.montantTotalEncaisseExternal) > 0 ? this.statsData.nombreTransactions : 0;
    },

    getExternalPaymentsTotal() {
      // Utilise maintenant le champ du backend
      if (!this.statsData) return '0 fcfa';

      // Si le nouveau champ existe
      if (this.statsData.totalComptantsEncaisses !== undefined) {
        return this.statsData.totalComptantsEncaisses;
      }

      // Fallback : calculer la somme
      console.warn('⚠️ totalComptantsEncaisses non trouvé, calcul de la somme');
      const cash = this.extractNumber(this.statsData.cashVentes);
      const wave = this.extractNumber(this.statsData.waveVentes);
      const om = this.extractNumber(this.statsData.omVentes);
      const autres = this.extractNumber(this.statsData.autreVentes);

      const total = cash + wave + om + autres;

      const formatter = new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });

      return formatter.format(total).replace(/\s/g, '.') + ' fcfa';
    },

    getExternalPaymentsTotalValue() {
      // Pour le calcul des pourcentages - retourne juste la valeur numérique
      if (!this.statsData) return 0;

      if (this.statsData.totalComptantsEncaisses !== undefined) {
        // Extraire la valeur numérique de totalComptantsEncaisses
        return this.extractNumber(this.statsData.totalComptantsEncaisses);
      }

      // Fallback - calculer la somme
      const cash = this.extractNumber(this.statsData.cashVentes);
      const wave = this.extractNumber(this.statsData.waveVentes);
      const om = this.extractNumber(this.statsData.omVentes);
      const autres = this.extractNumber(this.statsData.autreVentes);

      return cash + wave + om + autres;
    },

    // AJOUT: Calculer le total à reverser (comptants + recharges)
    calculateTotalToReverse() {
      if (!this.statsData) return '0 fcfa';

      // Total des comptants encaissés
      const totalComptants = this.extractNumber(this.statsData.totalComptantsEncaisses);

      // Montant total des recharges
      const totalRecharges = this.extractNumber(this.statsData.montantRecharges);

      // Somme totale à reverser
      const totalToReverse = totalComptants + totalRecharges;

      // Formater le montant
      const formatter = new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });

      return formatter.format(totalToReverse).replace(/\s/g, '.') + ' fcfa';
    },

    // AJOUT: Calculer le nombre de jours dans la période
    getPeriodDays() {
      if (!this.selectedDateRange.start || !this.selectedDateRange.end) return 0;

      const start = moment(this.selectedDateRange.start);
      const end = moment(this.selectedDateRange.end);

      return Math.abs(end.diff(start, 'days')) + 1;
    }
  }
};
</script>

<style scoped>
.toolbar .v-toolbar__content {
  padding: 0 !important;
}

.v-text-field {
  min-width: 222px;
}

.v-input--dense .v-input__control {
  min-height: 40px;
}

.v-input--dense .v-input__slot {
  min-height: 40px;
}

.pre-formatted {
  white-space: pre-wrap;
}

.multi-line {
  word-wrap: break-word;
}

/* Design moderne et glassmorphism */
.modern-summary-card {
  border-radius: 20px !important;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85));
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.card-header-gradient {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px 20px 0 0;
  padding: 24px 32px;
}

.header-content {
  display: flex;
  align-items: center;
}

.header-title {
  font-size: 24px;
  font-weight: 600;
  color: white;
  letter-spacing: 0.5px;
}

.metric-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7));
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(8px);
}

.metric-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.metric-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.4));
}

.metric-content {
  flex: 1;
}

.metric-value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 4px;
}

.metric-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

/* Glass cards pour ventes et recharges */
.glass-card {
  border-radius: 20px !important;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.sales-card {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(76, 175, 80, 0.05));
}

.recharge-card {
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.1), rgba(255, 152, 0, 0.05));
}

.glass-header {
  padding: 20px 24px;
  border-radius: 20px 20px 0 0;
  display: flex;
  align-items: center;
}

.sales-header {
  background: linear-gradient(135deg, #4CAF50, #45A049);
}

.recharge-header {
  background: linear-gradient(135deg, #FF9800, #F57C00);
}

.glass-title {
  font-size: 18px;
  font-weight: 600;
  color: white;
  letter-spacing: 0.3px;
}

.stats-overview {
  display: flex;
  justify-content: space-around;
  padding: 16px 0;
}

.overview-item {
  text-align: center;
}

.overview-number {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 8px;
}

.overview-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

/* Nouveau design pour la visualisation des données */
.data-visualization {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.3));
  border-radius: 16px;
  padding: 24px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.visualization-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  text-align: center;
}

.payment-stat-item {
  margin-bottom: 20px;
}

.payment-stat-item:last-child {
  margin-bottom: 0;
}

.payment-stat-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.payment-stat-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}

.cash-icon {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.2), rgba(76, 175, 80, 0.1));
}

.wave-icon {
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.2), rgba(33, 150, 243, 0.1));
}

.om-icon {
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.2), rgba(255, 152, 0, 0.1));
}

.other-icon {
  background: linear-gradient(135deg, rgba(158, 158, 158, 0.2), rgba(158, 158, 158, 0.1));
}

.payment-stat-name {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.payment-stat-amount {
  font-size: 16px;
  font-weight: 600;
}

.payment-stat-percent {
  font-size: 12px;
  color: #666;
  text-align: right;
  margin-top: 4px;
  font-weight: 500;
}

/* Timeline moderne */
.timeline-card {
  border-radius: 20px !important;
  background: linear-gradient(135deg, rgba(96, 125, 139, 0.1), rgba(96, 125, 139, 0.05));
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.timeline-container {
  text-align: center;
}

.timeline-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.timeline-content {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 600px;
  margin: 0 auto;
}

.timeline-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.timeline-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  margin-bottom: 12px;
}

.start-dot {
  background: linear-gradient(135deg, #4CAF50, #45A049);
  box-shadow: 0 0 20px rgba(76, 175, 80, 0.4);
}

.end-dot {
  background: linear-gradient(135deg, #F44336, #D32F2F);
  box-shadow: 0 0 20px rgba(244, 67, 54, 0.4);
}

.timeline-line {
  flex: 1;
  height: 3px;
  background: linear-gradient(90deg, #4CAF50, #F44336);
  margin: 0 32px;
  border-radius: 2px;
  position: relative;
  top: -20px;
}

.timeline-info {
  text-align: center;
}

.timeline-label {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.timeline-time {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

/* Indicateur de statut */
.status-indicator {
  display: flex;
  align-items: center;
  color: #009688;
  font-style: italic;
}

/* Responsive */
@media (max-width: 960px) {
  .timeline-content {
    flex-direction: column;
    gap: 16px;
  }

  .timeline-line {
    width: 3px;
    height: 40px;
    margin: 8px 0;
    background: linear-gradient(180deg, #4CAF50, #F44336);
    top: 0;
  }

  .metric-card {
    padding: 16px;
  }

  .metric-value {
    font-size: 20px;
  }

  .overview-number {
    font-size: 24px;
  }
}

/* AJOUT: Styles pour le toggle de mode de date avec loaders */
.date-mode-toggle {
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.15);
  border-radius: 30px !important;
  background: white;
}

.date-mode-toggle .v-btn {
  text-transform: none;
  font-weight: 600;
  font-size: 16px;
  padding: 0 32px;
  height: 48px !important;
  border-radius: 24px !important;
  transition: all 0.3s ease;
  position: relative;
}

.date-mode-toggle .v-btn:not(.v-btn--active):not(.v-btn--loading) {
  background: transparent !important;
  color: #666 !important;
}

.date-mode-toggle .v-btn--active:not(.v-btn--loading) {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.date-mode-toggle .v-btn--active:not(.v-btn--loading) .v-icon {
  color: white !important;
}

.date-mode-toggle .v-btn:hover:not(.v-btn--active):not(.v-btn--loading):not(.v-btn--disabled) {
  background-color: rgba(102, 126, 234, 0.08) !important;
}

.date-mode-toggle .v-btn--loading {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
}

.date-mode-toggle .v-btn--disabled:not(.v-btn--loading) {
  opacity: 0.6;
  pointer-events: none;
}

/* NOUVEAU: Styles pour le bouton de recherche */
.search-btn {
  text-transform: none !important;
  font-weight: 600;
  font-size: 16px;
  padding: 0 40px;
  height: 50px !important;
  border-radius: 25px !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
}

.search-btn:hover:not(.v-btn--disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.6);
}

.search-btn:disabled {
  opacity: 0.6 !important;
  background: #ccc !important;
  box-shadow: none !important;
}

.search-btn .v-icon {
  color: white !important;
}

/* Indicateur de période */
.period-indicator {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, rgba(96, 125, 139, 0.1), rgba(96, 125, 139, 0.05));
  border-radius: 12px;
  border-left: 4px solid #607D8B;
}
</style>